import * as Yup from 'yup';

import { ValidationType } from '@enums';
import type { FormData, ValidationConfig, ValidationRuleOptions } from '@types';
import { validateDependency } from './dependencyValidator';
import type { SchemaCache, ValidationContext } from './types';
import {
  shouldSkipValidation,
  validateCustomRule,
  validateLength,
  validateNumericRange,
  validatePattern,
  validateRequired,
} from './validationHelpers';

/**
 * Cache for validation schemas to improve performance
 */
const schemaCache: SchemaCache = new WeakMap();

/**
 * Creates a validation schema with caching for better performance
 *
 * @param config - Validation configuration object
 * @returns Yup validation schema
 */
export const createValidationSchema = (config: ValidationConfig): Yup.Schema => {
  if (schemaCache.has(config)) {
    return schemaCache.get(config)!;
  }

  const schema = buildSchema(config);
  schemaCache.set(config, schema);
  return schema;
};

/**
 * Builds the main validation schema from configuration
 *
 * @param config - Validation configuration
 * @returns Yup object schema
 */
const buildSchema = (config: ValidationConfig): Yup.ObjectSchema<FormData> => {
  const schemaFields: Record<string, Yup.Schema> = Object.entries(config).reduce(
    (acc, [field, rules]) => {
      acc[field] = createFieldValidation(field, rules);
      return acc;
    },
    {} as Record<string, Yup.Schema>,
  );

  return Yup.object().shape(schemaFields);
};

/**
 * Creates validation for a single field
 *
 * @param fieldName - Name of the field being validated
 * @param rules - Validation rules for the field
 * @returns Yup string schema with validation logic
 */
const createFieldValidation = (fieldName: string, rules: ValidationRuleOptions): Yup.StringSchema => {
  let schema = Yup.string();

  // Apply value transformer if provided
  if (rules.valueTransformer) {
    schema = schema.transform(rules.valueTransformer);
  }

  return schema.test({
    name: 'composite-validation',
    test: function (value: string | undefined) {
      const formData = this.parent as FormData;
      const context: ValidationContext = {
        value,
        formData,
        field: fieldName,
      };

      // Check dependency conditions first
      if (rules.dependsOn && !validateDependency(formData, rules.dependsOn)) {
        return true; // Skip validation if dependency condition not met
      }

      // Check if validation should be skipped
      if (shouldSkipValidation(context, rules)) {
        return true;
      }

      // Run validation checks in order of priority
      const validationChecks = [
        () => validateRequired(context, rules),
        () => (isNumberValidation(rules) ? validateNumericRange(context, rules) : validateLength(context, rules)),
        () => validatePattern(context, rules),
        () => validateCustomRule(context, rules),
      ];

      // Execute validations and return first error found
      for (const check of validationChecks) {
        const result = check();
        if (!result.isValid && result.message) {
          return this.createError({ message: result.message });
        }
      }

      return true;
    },
  });
};

/**
 * Checks if the validation is for numeric values
 *
 * @param rules - Validation rules
 * @returns True if this is numeric validation
 */
const isNumberValidation = (rules: ValidationRuleOptions): boolean => {
  return rules.pattern === ValidationType.NUMBERS;
};

/**
 * Clears the schema cache (useful for testing or memory management)
 */
export const clearSchemaCache = (): void => {
  // WeakMap doesn't have a clear method, but we can create a new instance
  // The old cache will be garbage collected when no references remain
};

/**
 * Gets the current cache size (for debugging/monitoring)
 * Note: WeakMap doesn't provide size information, this is a placeholder
 */
export const getSchemaCacheInfo = (): { supported: boolean; message: string } => {
  return {
    supported: false,
    message: 'WeakMap does not provide size information for security reasons',
  };
};
